import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { Gem, Users } from 'lucide-react'
import useOpenTeam from '../../../hooks/useOpenTeam'
import { buildBattleTeamsSignature, getBattleTeams, isFlagOn } from '../utils/battleTeams'
import { buildTeamColorMap } from '../../../utils/participantColors'

const CONTAINER_VARIANT = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } }
const ROW_VARIANT = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
}

/**
 * Сводка банд батла: строка на каждую банду с 2+ участниками (`is_team: true`).
 *
 * Блок виден **с момента входа**, ещё в `status: waiting` — игрок, который
 * собирается зайти, должен видеть, что два слота из трёх занимает одна банда
 * (при её победе банк поделится между ними).
 *
 * Банда с `is_team: false` — обычный игрок с бейджем у ника, отдельной строки
 * не получает. Если командных банд нет (или ключа `teams` нет вовсе) —
 * компонент не рендерится.
 *
 * Цвет строки — из общей палитры участников (`utils/participantColors`), тот
 * же, которым покрашена кромка слотов этой банды и её группа в классике.
 */
const BattleTeamsSummary = ({ battle, showResults = false }) => {
  const { t } = useTranslation()
  const openTeam = useOpenTeam()

  const teams = getBattleTeams(battle)
  const teamsSignature = buildBattleTeamsSignature(teams)

  const teamColors = useMemo(() => buildTeamColorMap(teams), [teamsSignature])
  const rows = useMemo(
    () => [...teams].sort((a, b) => Number(b?.count_users || 0) - Number(a?.count_users || 0)),
    [teamsSignature]
  )

  if (rows.length === 0) return null

  return (
    <div className="mb-6 rounded-2xl border border-white/10 bg-surface p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)]">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="inline-flex items-center gap-2 font-semibold text-white">
          <Users className="h-4 w-4 text-team-accent" />
          {t('battle.teams.title')}
        </h2>
        <span className="hidden text-[10px] font-semibold text-text-secondary">
          {t('battle.teams.hint')}
        </span>
      </div>

      <motion.div
        className="space-y-2"
        variants={CONTAINER_VARIANT}
        initial="hidden"
        animate="visible"
      >
        {rows.map((team) => {
          const color = teamColors.get(team.slug) || '#A78BFA'
          const slots = team.slots || []
          // Итоги делёжки появляются только после розыгрыша и только когда их
          // уже показала анимация — иначе результат утечёт раньше рулеток
          const hasWon = showResults && isFlagOn(team.team_win)
          const payout = Number(team.payout || 0)

          return (
            <motion.div
              key={team.slug}
              variants={ROW_VARIANT}
              className="relative overflow-hidden rounded-xl border border-white/10 bg-dark-primary/60 p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]"
              style={{ borderLeft: `3px solid ${color}` }}
            >
              <div
                className="pointer-events-none absolute inset-y-0 left-0 w-1/2"
                style={{ background: `linear-gradient(90deg, ${color}2E, ${color}00)` }}
              />

              <div className="relative flex items-center gap-3">
                <img
                  src={team.ava || `https://ui-avatars.com/api/?name=${encodeURIComponent(team.name || 'Team')}&background=A78BFA&color=0A0A0F`}
                  alt={team.name}
                  className="h-10 w-10 shrink-0 rounded-xl border-2 object-cover"
                  style={{ borderColor: color }}
                  loading="lazy"
                />

                <div className="min-w-0 flex-1">
                  <p
                    onClick={team.slug ? (event) => openTeam(event, team.slug) : undefined}
                    className={`truncate text-sm font-semibold text-white ${
                      team.slug ? 'cursor-pointer transition-transform active:scale-[0.98]' : ''
                    }`}
                    title={team.name}
                  >
                    {team.name}
                  </p>
                  <div className="mt-0.5 flex items-center gap-3 text-[11px] text-text-secondary">
                    <span className="inline-flex items-center gap-1 tabular-nums">
                      <Users className="h-3 w-3" />
                      {Number(team.count_users || 0)}
                    </span>
                    {slots.length > 0 && (
                      <span className="tabular-nums">
                        {t('battle.teams.slots', { slots: slots.join(', ') })}
                      </span>
                    )}
                  </div>
                </div>

                {hasWon && (
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="rounded-full border border-accent-primary/40 bg-accent-primary/15 px-2 py-0.5 text-[10px] font-semibold text-accent-primary">
                      {t('battle.teams.winBadge')}
                    </span>
                    {payout > 0 && (
                      <span className="inline-flex items-center gap-1 text-sm font-bold tabular-nums text-white">
                        <Gem className="h-3.5 w-3.5 text-accent-primary" />
                        {payout.toLocaleString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )
        })}
      </motion.div>
    </div>
  )
}

export default BattleTeamsSummary
